import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button, CssBaseline, TextField, Container, Paper, Checkbox, FormGroup,
  FormControlLabel, Select, MenuItem, Typography, RadioGroup, Radio, Box
} from '@mui/material';
import backgroundImage from "../../img/LEVLR-Login-Background.jpg";
import levlr_logo from "../../img/LEVLR Logo_CMYK.png";

const PostRegistration = () => {
  const navigate = useNavigate();
  const user = useSelector(state => state.user); 
  const userId = user?._id;
  const additionalAnswered = user?.additional_questions_answered;
  const status = user?.baseSubscription?.status;
  
  // Redirect logic
  useEffect(() => {
    console.log("user: ", user);
  
    // Wait until user is loaded and has a valid _id
    if (!user || !user._id) navigate('/register', { replace: true });
  
  }, [user]);
  

  const [form, setForm] = useState({
    role: [],
    otherRole: '',
    primaryDecisionMaker: '',
    annualRevenue: '',
    goals: [],
    otherGoal: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const roles = [
    'Independent Restorer',
    'Franchised Restorer',
    'Independent Adjuster',
    'Public Adjuster',
    'Captive/Carrier Adjuster',
    'Other',
  ];

  const revenues = [
    '<$500,000',
    '$500,001-$1M',
    '$1M-$3M',
    '$3M+',
  ];

  const goalsOptions = [
    'Time Savings',
    'Increase Accuracy',
    'Decision Making Assistance',
    'Report Generation',
    'Justifications for Line Items',
    'Other',
  ];

  const handleCheckboxChange = (field, value) => {
    setForm(prev => {
      const updated = prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value];
      return { ...prev, [field]: updated };
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.role.length) newErrors.role = 'Please select at least one role.';
    if (form.role.includes('Other') && !form.otherRole.trim()) newErrors.otherRole = 'Please specify your role.';
    if (!form.primaryDecisionMaker) {
        newErrors.primaryDecisionMaker = 'Please select Yes or No.';
      }
      
    if (!form.annualRevenue) newErrors.annualRevenue = 'Please select your annual revenue.';
    if (!form.goals.length) newErrors.goals = 'Please select at least one goal.';
    if (form.goals.includes('Other') && !form.otherGoal.trim()) newErrors.otherGoal = 'Please specify your goal.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        userId,
        roleSelections: form.role,
        otherRole: form.otherRole,
        primaryDecisionMaker: form.primaryDecisionMaker === 'yes',
        annualRevenue: form.annualRevenue,
        goals: form.goals,
        otherGoal: form.otherGoal,
        additional_questions_answered: true
      };

      const res = await fetch(process.env.REACT_APP_SAVE_REG_ROUTE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to save");

        navigate('/createNewComparison', { replace: true });

    } catch (err) {
      console.error(err);
      alert("There was an error saving your details.");
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    backGround: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundImage: `url(${backgroundImage})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      minHeight: '100vh',
      width: '100%',
      padding: '2% 0',
    },
    paper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'rgba(255,255,255,0.9)',
      width: '90%',
      maxWidth: 700,
      minHeight: 400,
      maxHeight: '90vh',
      padding: '3rem 2rem',
      margin: '0 auto',
      borderRadius: 8,
      overflowY: 'auto',
    },
    section: {
      width: '100%',
      padding: '1rem',
      marginBottom: '2rem',
      backgroundColor: '#f7f9fa',
      borderRadius: 6,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    textField: {
      width: '100%',
      marginBottom: '1rem',
      backgroundColor: '#eff0f0',
      borderRadius: 4,
    },
    button: {
      width: '60%',
      color: '#fff',
      margin: '8px 0',
      borderRadius: 4,
    },
    submitBtn: {
      backgroundColor: '#2fb7b7',
      '&:hover': { backgroundColor: '#01656a' },
    },
    logo: { width: '40%', height: 'auto', marginBottom: '1.5rem' },
    errorText: { color: 'red', marginTop: 4, fontSize: '0.85rem' },
    questionTitle: { fontWeight: 600, marginBottom: '0.5rem', color: '#01656a' },
  };

  // Only render form if user is loaded and hasn't answered


  return (
    <div style={styles.backGround}>
      <Container component="main">
        <CssBaseline />
        <Paper style={styles.paper}>
          <img src={levlr_logo} alt="Levlr Logo" style={styles.logo} />

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }} noValidate>

            {/* Role Section */}
            <Box style={styles.section}>
              <Typography style={styles.questionTitle}>Which best describes you?</Typography>
              <FormGroup>
                {roles.map(role => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={form.role.includes(role)}
                        onChange={() => handleCheckboxChange('role', role)}
                        sx={{ color: '#01656a', '&.Mui-checked': { color: '#2fb7b7' } }}
                      />
                    }
                    label={role}
                  />
                ))}
                {form.role.includes('Other') && (
                  <TextField
                    label="Please specify"
                    name="otherRole"
                    value={form.otherRole}
                    onChange={handleInputChange}
                    style={styles.textField}
                  />
                )}
              </FormGroup>
              {errors.role && <div style={styles.errorText}>{errors.role}</div>}
              {errors.otherRole && <div style={styles.errorText}>{errors.otherRole}</div>}
            </Box>

            {/* Decision Maker Section */}
            {/* Decision Maker Section - native radios, custom styled */}
<Box style={styles.section}>
  <Typography style={styles.questionTitle}>
    Are you the primary decision maker?
  </Typography>

{/* Inline CSS for the custom radio */}
<style>{`
  /* Row container */
  .custom-radio-row { 
    display: flex; 
    gap: 1.25rem; 
    align-items: center; 
    margin-top: 8px; 
  }

  /* Label */
  .custom-radio-label { 
    display: inline-flex; 
    align-items: center; 
    gap: 8px; 
    cursor: pointer; 
    user-select: none; 
    font-size: 1rem; 
    color: #000; /* text color black */ 
    position: relative; /* allow absolute positioning inside */
  }

  /* Hide default input but keep it in flow for hover/focus */
  .custom-radio-input { 
    position: absolute; 
    opacity: 0; 
    width: 20px; 
    height: 20px; 
    margin: 0; 
    cursor: pointer; 
  }

  /* Outer circle */
  .radio-mark {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid #bfcfd0; /* neutral border */
    background: white;
    display: inline-block;
    position: relative;
    flex-shrink: 0;
    transition: border-color 120ms ease;
  }

  /* Inner dot */
  .radio-mark::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 120ms ease;
    background: #00b3b5;
  }

  /* Checked state */
  .custom-radio-input:checked + .radio-mark {
    border-color: #00b3b5;
    background: rgba(0,179,181,0.06); /* subtle highlight */
  }

  .custom-radio-input:checked + .radio-mark::after {
    transform: translate(-50%, -50%) scale(1);
  }

  /* Hover */
  .custom-radio-label:hover .radio-mark {
    border-color: #00b3b5;
  }

  /* Focus */
  .custom-radio-input:focus + .radio-mark {
    outline: 2px solid rgba(0,179,181,0.18);
    outline-offset: 2px;
  }
`}</style>

  <div className="custom-radio-row">
    <label className="custom-radio-label">
      <input
        type="radio"
        name="primaryDecisionMaker"
        value="yes"
        checked={form.primaryDecisionMaker === 'yes'}
        onChange={handleInputChange}
        className="custom-radio-input"
      />
      <span className="radio-mark" aria-hidden="true" />
      <span style={{color: 'black'}}>Yes</span>
    </label>

    <label className="custom-radio-label">
      <input
        type="radio"
        name="primaryDecisionMaker"
        value="no"
        checked={form.primaryDecisionMaker === 'no'}
        onChange={handleInputChange}
        className="custom-radio-input"
      />
      <span className="radio-mark" aria-hidden="true" />
      <span style={{color: 'black'}}>No</span>
    </label>
  </div>

  {errors.primaryDecisionMaker && (
    <div style={styles.errorText}>{errors.primaryDecisionMaker}</div>
  )}
</Box>

            {/* Annual Revenue Section */}
            <Box style={styles.section}>
              <Typography style={styles.questionTitle}>What is your annual revenue?</Typography>
              <Select
                name="annualRevenue"
                value={form.annualRevenue}
                onChange={handleInputChange}
                style={styles.textField}
              >
                {revenues.map(rev => (
                  <MenuItem key={rev} value={rev}>{rev}</MenuItem>
                ))}
              </Select>
              {errors.annualRevenue && <div style={styles.errorText}>{errors.annualRevenue}</div>}
            </Box>

            {/* Goals Section */}
            <Box style={styles.section}>
              <Typography style={styles.questionTitle}>What are your main goals for using LEVLR? (Select all that apply)</Typography>
              <FormGroup>
                {goalsOptions.map(goal => (
                  <FormControlLabel
                    key={goal}
                    control={
                      <Checkbox
                        checked={form.goals.includes(goal)}
                        onChange={() => handleCheckboxChange('goals', goal)}
                        sx={{ color: '#01656a', '&.Mui-checked': { color: '#2fb7b7' } }}
                      />
                    }
                    label={goal}
                  />
                ))}
                {form.goals.includes('Other') && (
                  <TextField
                    label="Please specify your goal"
                    name="otherGoal"
                    value={form.otherGoal}
                    onChange={handleInputChange}
                    style={styles.textField}
                  />
                )}
              </FormGroup>
              {errors.goals && <div style={styles.errorText}>{errors.goals}</div>}
              {errors.otherGoal && <div style={styles.errorText}>{errors.otherGoal}</div>}
            </Box>

            <Button
              type="submit"
              variant="contained"
              style={{ ...styles.button, ...styles.submitBtn }}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Submit"}
            </Button>

          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default PostRegistration;
